import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';



type IconProps = { size?: number; style?: React.CSSProperties };

const svg =
  (iconColor: string, path: React.ReactNode, extraProps: Record<string, unknown> = {}) =>
  ({ size = 20, style }: IconProps) => (
    <svg
      width={size}
      height={size}
      style={style}
      viewBox="0 0 24 24"
      fill="none"
      stroke={iconColor}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...extraProps}
    >
      {path}
    </svg>
  );

/** Filled shapes (dots, chips) inside an icon use its own accent as `fill`
    instead of `stroke="none"` scattered per-path — same color either way,
    written where the icon is defined instead of at each call site. */
const filled = (color: string) => ({ fill: color, stroke: 'none' as const });

/* ── Layout (amber family) ──────────────────────────────────────────── */

export const OneColumnIcon = svg('#f59e0b', <rect x="3.5" y="5" width="17" height="14" rx="2" />);

export const TwoColumnIcon = svg(
  '#f59e0b',
  <>
    <rect x="3.5" y="5" width="17" height="14" rx="2" />
    <path d="M12 5v14" />
  </>,
);

export const ThreeColumnIcon = svg(
  '#f59e0b',
  <>
    <rect x="3.5" y="5" width="17" height="14" rx="2" />
    <path d="M9.5 5v14M14.5 5v14" />
  </>,
);


export const LineBreakIcon = svg(
  '#f59e0b',
  <>
    <path d="M4 8h6M14 8h6" />
    <path d="M4 16h6M14 16h6" />
    <path d="M9 5.5 12 8l-3 2.5M15 13.5 12 16l3 2.5" />
  </>,
);

/* ── Basic Info ─────────────────────────────────────────────────────── */

export const EmailIcon = svg(
  '#f97316',
  <>
    <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
    <path d="m3.5 6.5 8.5 6.5 8.5-6.5" />
  </>,
);

export const NameIcon = svg(
  '#3b82f6',
  <>
    <circle cx="12" cy="7.5" r="3.5" />
    <path d="M4.5 20c0-4.1 3.4-6.5 7.5-6.5s7.5 2.4 7.5 6.5" />
  </>,
);

export const NumberIcon = svg('#10b981', <path d="M9.5 4 6.5 20M17.5 4l-3 16M4 9h16M3 15h16" />);

export const AddressIcon = svg(
  '#ef4444',
  <>
    <path d="M12 21.5c4-3.8 7-7.4 7-11.2a7 7 0 1 0-14 0c0 3.8 3 7.4 7 11.2Z" />
    <circle cx="12" cy="10.2" r="2.4" {...filled('#ef4444')} />
  </>,
);

export const PhoneIcon = svg(
  '#14b8a6',
  <path d="M7 3.5h3l1.2 4.2-2.3 1.8a13 13 0 0 0 5.6 5.6l1.8-2.3 4.2 1.2v3a1.8 1.8 0 0 1-2 1.8A16 16 0 0 1 5.2 5.5a1.8 1.8 0 0 1 1.8-2Z" />,
);

export const DatePickerIcon = svg(
  '#d97706',
  <>
    <rect x="3.5" y="5.5" width="17" height="15" rx="2" />
    <path d="M3.5 10h17M8 3.5v4M16 3.5v4" />
    <circle cx="8.2" cy="14.2" r="1" {...filled('#d97706')} />
    <circle cx="12" cy="14.2" r="1" {...filled('#d97706')} />
    <circle cx="15.8" cy="14.2" r="1" {...filled('#d97706')} />
  </>,
);

export const HyperLinkIcon = svg(
  '#8b5cf6',
  <>
    <path d="M9.5 14.5 14.5 9.5" />
    <path d="M11 7.2 12.4 5.8a3.6 3.6 0 0 1 5.1 5.1l-1.4 1.4" />
    <path d="M13 16.8 11.6 18.2a3.6 3.6 0 0 1-5.1-5.1l1.4-1.4" />
  </>,
);

/* ── Date & Time ────────────────────────────────────────────────────── */

export const TimeIcon = svg(
  '#0ea5e9',
  <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </>,
);

export const DateTimeIcon = svg(
  '#6366f1',
  <>
    <path d="M20 11V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h5.5" />
    <path d="M4 10h16M8 3.5v4M16 3.5v4" />
    <circle cx="17" cy="17" r="4.2" />
    <path d="M17 15v2l1.4.9" />
  </>,
);

export const MonthYearIcon = svg(
  '#eab308',
  <>
    <rect x="3.5" y="5.5" width="17" height="15" rx="2" />
    <path d="M3.5 10h17M8 3.5v4M16 3.5v4" />
    <path d="M8 14.5h8" />
  </>,
);

export const SlotBookingIcon = svg(
  '#22c55e',
  <>
    <rect x="3.5" y="5.5" width="17" height="15" rx="2" />
    <path d="M3.5 10h17M8 3.5v4M16 3.5v4" />
    <path d="M8.5 15.2 11 17.5l4.5-5" />
  </>,
);

/* ── Textbox ────────────────────────────────────────────────────────── */


export const SingleLineIcon = svg(
  '#f43f5e',
  <>
    <rect x="2.5" y="6" width="19" height="12" rx="2.5" />
    <path d="M11.8 9.5v5" />
    <path d="M6 9.5h3M6 14.5h2.4" />
  </>,
);

export const MultiLineIcon = svg(
  '#a855f7',
  <>
    <rect x="3" y="4.5" width="18" height="15" rx="2" />
    <path d="M6.5 9h11M6.5 12.5h11M6.5 16h6" />
  </>,
);

export const HeaderIcon = svg('#ec4899', <path d="M5 5v14M15 5v14M5 12h10M18.5 19V9.8L16.5 11" />);

export const ParagraphIcon = svg('#64748b', <path d="M4 6h16M4 10.5h16M4 15h16M4 19.5h9" />);

export const MultiSelectIcon = svg(
  '#f59e0b',
  <>
    <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
    <path d="M6.2 8.6l1.3 1.3 2.2-2.4" />
    <path d="M6.2 13.4l1.3 1.3 2.2-2.4" />
    <path d="M12.5 8.8h5.3M12.5 13.6h5.3" />
  </>,
);

export const ChipSelectIcon = svg(
  '#a855f7',
  <>
    <rect x="2.5" y="6" width="19" height="12" rx="2" />
    <rect x="5" y="9.2" width="6" height="5.6" rx="2.8" />
    <rect x="12.5" y="9.2" width="4.6" height="5.6" rx="2.8" />
  </>,
);

/* ── Choices ────────────────────────────────────────────────────────── */

export const RadioIcon = svg(
  '#06b6d4',
  <>
    <circle cx="12" cy="12" r="8.5" />
    <circle cx="12" cy="12" r="3.2" {...filled('#06b6d4')} />
  </>,
);

export const CheckboxIcon = svg(
  '#0891b2',
  <>
    <rect x="3.5" y="3.5" width="17" height="17" rx="3.5" />
    <path d="m7.8 12.2 2.6 2.6L16.5 9" />
  </>,
);

/* ── Rating & Scales ────────────────────────────────────────────────── */

export const RatingIcon = svg(
  '#f59e0b',
  <path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.9L12 3.5Z" />,
);

export const OpinionScaleIcon = svg(
  '#10b981',
  <>
    <rect x="2.5" y="8" width="4.6" height="8" rx="1.4" />
    <rect x="9.7" y="8" width="4.6" height="8" rx="1.4" {...filled('#10b981')} />
    <rect x="16.9" y="8" width="4.6" height="8" rx="1.4" />
  </>,
);

export const SliderIcon = svg(
  '#6366f1',
  <>
    <path d="M3 12h18" />
    <circle cx="9" cy="12" r="3" {...filled('#6366f1')} />
  </>,
);

/* ── Media ──────────────────────────────────────────────────────────── */

export const ImageIcon = svg(
  '#ec4899',
  <>
    <rect x="3" y="4.5" width="18" height="15" rx="2" />
    <circle cx="8.5" cy="9.5" r="1.7" />
    <path d="m4.5 17 4.6-4.6a1.8 1.8 0 0 1 2.6 0L14 15l2.5-2.5a1.8 1.8 0 0 1 2.6 0l1.9 1.9" />
  </>,
);

/* ── Actions & Payments ─────────────────────────────────────────────── */

export const ButtonIcon = svg(
  '#8b5cf6',
  <>
    <rect x="2.5" y="8" width="19" height="8" rx="4" />
    <path d="M8.5 12h7" />
  </>,
);

/* A card face + a chip, the universal "payment" cue — clearer than a plain
   rectangle-with-bar, which read as a generic panel rather than a card. */
export const PaymentIcon = svg(
  '#7c3aed',
  <>
    <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
    <path d="M2.5 9.5h19" />
    <rect x="5.5" y="13" width="3.4" height="2.4" rx="0.6" {...filled('#7c3aed')} />
  </>,
);

export const KycIcon = svg(
  '#059669',
  <>
    <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
    <circle cx="8.7" cy="10.3" r="2.1" />
    <path d="M5.2 15.8a3.6 3.6 0 0 1 7 0" />
    <path d="M14.2 9.3h4.3M14.2 12.6h4.3" />
  </>,
);

export const ESignIcon = svg(
  '#db2777',
  <>
    <path d="M3 17c2.8.3 3-8.5 5.5-8.5s2 8 4.5 6.8" />
    <path d="M3 21h13" />
    <path d="m15.5 14 4.6-4.6a1.7 1.7 0 0 1 2.5 2.5L18 16.5l-3 .6.5-3.1Z" />
  </>,
);

export const AiGeneratorIcon = svg(
  '#5550ff',
  <>
    <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3L12 3z" />
  </>,
);

export const WorkflowPreviewIcon = svg(
  '#0f766e',
  <>
    <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" />
    <circle cx="12" cy="12" r="3" />
  </>,
);

export const CrmSelectIcon = svg(
  '#0ea5e9',
  <>
    <circle cx="11" cy="11" r="6" />
    <path d="m20 20-4.2-4.2" />
    <path d="M8.5 11h5M11 8.5v5" />
  </>,
);

/* ── Quiz & Results ─────────────────────────────────────────────────── */

export const LoadingAnalysisIcon = svg(
  '#f97316',
  <>
    <path d="M4 7h16M4 12h16M4 17h16" strokeOpacity="0.35" />
    <path d="M4 7h16M4 12h10M4 17h4" />
  </>,
);

export const ResultSummaryIcon = svg(
  '#10b981',
  <>
    <rect x="3.5" y="3.5" width="17" height="17" rx="3" />
    <path d="M7 15.5h10" />
    <circle cx="12.5" cy="15.5" r="1.6" {...filled('#10b981')} />
    <path d="M7 8.5h6M7 11h4" />
  </>,
);

export const ProgressChartIcon = svg(
  '#3b82f6',
  <>
    <path d="M3.5 20.5h17M3.5 20.5v-17" />
    <path d="M6 17c4 0 6-1.5 8-5s3-5 5-6" />
    <circle cx="19" cy="6" r="1.5" {...filled('#3b82f6')} />
  </>,
);

/* ── Section groupings ──────────────────────────────────────────────── */

export interface ToolboxIconEntry {
  /** matches the `key` of the entry in formItems.ts */
  key: string;
  label: string;
  Icon: React.FC<IconProps>;
}

export interface ToolboxSection {
  title: string;
  items: ToolboxIconEntry[];
}

export const TOOLBOX_SECTIONS: ToolboxSection[] = [
  {
    title: 'Layout',
    items: [
      { key: 'ColumnRow', label: '1-Column', Icon: OneColumnIcon },
      { key: 'TwoColumnRow', label: '2-Column', Icon: TwoColumnIcon },
      { key: 'ThreeColumnRow', label: '3-Column', Icon: ThreeColumnIcon },
      { key: 'LineBreak', label: 'Line Break', Icon: LineBreakIcon },
    ],
  },
  {
    title: 'Basic Info',
    items: [
      { key: 'NameInput', label: 'Name', Icon: NameIcon },
      { key: 'EmailInput', label: 'Email', Icon: EmailIcon },
      { key: 'NumberInput', label: 'Number', Icon: NumberIcon },
      { key: 'AddressInput', label: 'Address', Icon: AddressIcon },
      { key: 'PhoneInput', label: 'Phone', Icon: PhoneIcon },
      { key: 'HyperLink', label: 'Link', Icon: HyperLinkIcon },
    ],
  },
  {
    title: 'Date & Time',
    items: [
      { key: 'DateInput', label: 'Date', Icon: DatePickerIcon },
      { key: 'TimeInput', label: 'Time', Icon: TimeIcon },
      { key: 'DateTimeInput', label: 'Date-Time', Icon: DateTimeIcon },
      { key: 'MonthYearInput', label: 'Month-Year', Icon: MonthYearIcon },
      { key: 'SlotBooking', label: 'Slot Booking', Icon: SlotBookingIcon },
    ],
  },
  {
    title: 'Textbox',
    items: [
      { key: 'TextInput', label: 'Text Input', Icon: SingleLineIcon },
      { key: 'TextArea', label: 'Text Area', Icon: MultiLineIcon },
      { key: 'Header', label: 'Header Text', Icon: HeaderIcon },
      { key: 'Paragraph', label: 'Paragraph', Icon: ParagraphIcon },
      { key: 'Tags', label: 'Multi Select', Icon: MultiSelectIcon },
      { key: 'ChipSelect', label: 'Chip Select', Icon: ChipSelectIcon },
    ],
  },
  {
    title: 'Choices',
    items: [
      { key: 'RadioButtons', label: 'Radio', Icon: RadioIcon },
      { key: 'Checkboxes', label: 'Checkbox', Icon: CheckboxIcon },
      { key: 'CrmAutocomplete', label: 'CRM Select', Icon: CrmSelectIcon },
    ],
  },
  {
    title: 'Rating & Scales',
    items: [
      { key: 'RatingInput', label: 'Rating', Icon: RatingIcon },
      { key: 'OpinionScale', label: 'Opinion Scale', Icon: OpinionScaleIcon },
      { key: 'SliderInput', label: 'Slider', Icon: SliderIcon },
    ],
  },
  {
    title: 'Media',
    items: [{ key: 'Image', label: 'Image', Icon: ImageIcon }],
  },
  {
    title: 'Actions',
    items: [
      { key: 'CustomButton', label: 'Button', Icon: ButtonIcon },
      { key: 'AiGenerator', label: 'AI Generator', Icon: AiGeneratorIcon },
      { key: 'WorkflowPreview', label: 'Workflow Preview', Icon: WorkflowPreviewIcon },
      { key: 'PaymentSection', label: 'Payment', Icon: PaymentIcon },
      { key: 'KycButton', label: 'KYC', Icon: KycIcon },
      { key: 'ESignButton', label: 'eSign', Icon: ESignIcon },
    ],
  },
  {
    title: 'Quiz & Results',
    items: [
      { key: 'LoadingAnalysis', label: 'Loading Analysis', Icon: LoadingAnalysisIcon },
      { key: 'ResultSummary', label: 'Result Summary', Icon: ResultSummaryIcon },
      { key: 'ProgressChart', label: 'Progress Chart', Icon: ProgressChartIcon },
    ],
  },
];

/** Flat lookup by item key, for rendering a single icon anywhere. */
export const TOOLBOX_ICON_BY_KEY: Record<string, React.FC<IconProps>> =
  TOOLBOX_SECTIONS.reduce(
    (acc, section) => {
      section.items.forEach((item) => {
        acc[item.key] = item.Icon;
      });
      return acc;
    },
    {} as Record<string, React.FC<IconProps>>,
  );

/**
 * react-form-builder2 renders each toolbox card's glyph as `<i className={item.icon}>`
 * with no children slot — `item.icon` in formItems.ts is `"ms-icon ms-<marker>"`,
 * a leftover from when the marker classes selected a Material Symbols ligature
 * via CSS. That marker is still a stable, unique-per-item class in the live DOM,
 * so it's reused here as the lookup key for the real SVG replacing it.
 */
export const TOOLBOX_ICON_BY_MARKER_CLASS: Record<string, React.FC<IconProps>> = {
  'ms-column': OneColumnIcon,
  'ms-two-column': TwoColumnIcon,
  'ms-three-column': ThreeColumnIcon,
  'ms-line-break': LineBreakIcon,
  'ms-name': NameIcon,
  'ms-email': EmailIcon,
  'ms-number': NumberIcon,
  'ms-address': AddressIcon,
  'ms-phone': PhoneIcon,
  'ms-link': HyperLinkIcon,
  'ms-date': DatePickerIcon,
  'ms-time': TimeIcon,
  'ms-datetime': DateTimeIcon,
  'ms-month': MonthYearIcon,
  'ms-slot-booking': SlotBookingIcon,
  'ms-text-input': SingleLineIcon,
  'ms-textarea': MultiLineIcon,
  'ms-header': HeaderIcon,
  'ms-paragraph': ParagraphIcon,
  'ms-tags': MultiSelectIcon,
  'ms-chip-select': ChipSelectIcon,
  'ms-radio': RadioIcon,
  'ms-checkbox': CheckboxIcon,
  'ms-rating': RatingIcon,
  'ms-opinion-scale': OpinionScaleIcon,
  'ms-slider': SliderIcon,
  'ms-image': ImageIcon,
  'ms-button': ButtonIcon,
  'ms-ai-generator': AiGeneratorIcon,
  'ms-crm-autocomplete': CrmSelectIcon,
  'ms-workflow-preview': WorkflowPreviewIcon,
  'ms-payment': PaymentIcon,
  'ms-kyc': KycIcon,
  'ms-esign': ESignIcon,
  'ms-loading-analysis': LoadingAnalysisIcon,
  'ms-result-summary': ResultSummaryIcon,
  'ms-progress-chart': ProgressChartIcon,
};


export const ToolboxIconPortals: React.FC<{ scanKey?: unknown }> = ({ scanKey }) => {
  const [targets, setTargets] = useState<{ el: Element; marker: string }[]>([]);

  useEffect(() => {
    const scan = () => {
      const toolbar = document.querySelector('.react-form-builder-toolbar');
      if (!toolbar) {
        setTargets((prev) => (prev.length ? [] : prev));
        return;
      }

      const found: { el: Element; marker: string }[] = [];
      Object.keys(TOOLBOX_ICON_BY_MARKER_CLASS).forEach((marker) => {
        toolbar.querySelectorAll(`i.${marker}`).forEach((el) => {
          found.push({ el, marker });
        });
      });
      setTargets((prev) =>
        prev.length === found.length && prev.every((p, i) => p.el === found[i].el)
          ? prev
          : found,
      );
    };

    scan();
    const observer = new MutationObserver(scan);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [scanKey]);

  return (
    <>
      {targets.map(({ el, marker }, i) => {
        const Icon = TOOLBOX_ICON_BY_MARKER_CLASS[marker];
        if (!Icon) return null;
        return createPortal(
          <Icon size={26} style={{ pointerEvents: 'none' }} />,
          el,
          `${marker}-${i}`,
        );
      })}
    </>
  );
};
